<template>
  <div class="payment-container" v-frag>
    <CardIeaAppMain colorCard="transparent" title="" class="premium-wrapper" v-if="!mantenimiento">
      <v-col cols="12" class="pa-0">
        
        <!-- Hero Header -->
        <div class="hero-header glass-panel mb-4 py-3 px-6 mx-2 mx-sm-4 mt-4">
          <div class="d-flex align-center justify-space-between w-100 flex-wrap">
            <div class="d-flex align-center mb-2 mb-md-0">
              <v-btn icon dark color="primary" class="mr-4 glass-btn" @click="$router.go(-1)" v-if="user && user.IDStB">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <div>
                <h1 class="text-h5 font-weight-bold primary-text mb-0">Efectuar Pago</h1>
                <p class="text-caption grey--text text--darken-1 mb-0 d-none d-sm-block">Gestión de cuotas y aranceles académicos de tu curso</p>
                <p class="text-caption grey--text text--darken-1 mb-0 d-block d-sm-none">Pago de cuotas y aranceles</p>
              </div>
            </div>
            <v-icon size="44" color="primary" class="opacity-30 d-none d-sm-block">mdi-credit-card-outline</v-icon>
          </div>
        </div>

        <!-- Main Content Card -->
        <v-card class="glass-card rounded-xl overflow-hidden mx-2 mx-sm-4 mb-6" elevation="10">
          <div class="pa-4 pa-sm-6 content-area bg-light">

            <!-- Alerts Section -->
            <v-alert dense outlined type="info" class="rounded-xl mb-4 bg-white" icon="mdi-information-outline">
              <div class="text-body-2">
                Los pagos con la pasarela de pago <strong>Qobra</strong> pueden demorar unos minutos en verse reflejados.
              </div>
            </v-alert>

            <v-alert v-show="isSuscribedDA" prominent outlined type="warning" class="rounded-xl mb-4 bg-white" icon="mdi-shield-check">
              <div class="text-body-2">
                <strong>Usted está adherido al débito automático en Mercado Pago.</strong> Si el primer intento de débito falla, se volverá a intentar tres veces en los próximos días (último intento el día 15 de cada mes). En caso de no visualizar su pago acreditado, por favor genere una consulta en "Informar un pago".
              </div>
            </v-alert>

            <v-alert v-show="isSuscribedMB" prominent outlined type="warning" class="rounded-xl mb-4 bg-white" icon="mdi-shield-check">
              <div class="text-body-2">
                <strong>Usted está adherido al débito automático en Mobbex.</strong> Si el primer intento de débito falla, se volverá a intentar tres veces en los próximos días (último intento el día 15 de cada mes). En caso de no visualizar su pago acreditado, por favor genere una consulta en "Informar un pago".
              </div>
            </v-alert>

            <!-- Course Selector Box -->
            <v-card class="rounded-xl border-light pa-5 mb-5 bg-white" elevation="0">
              <div class="d-flex align-center mb-3">
                <v-avatar color="primary" size="36" class="mr-3">
                  <v-icon color="white" small>mdi-school</v-icon>
                </v-avatar>
                <div>
                  <div class="text-subtitle-1 font-weight-bold primary-text">Curso a pagar</div>
                  <div class="text-caption grey--text">Selecciona tu carrera o programa académico</div>
                </div>
              </div>

              <v-select
                v-model="curso"
                :items="cursos"
                :item-text="item => item.cruso_data ? (item.cruso_data.Feriado || item.cruso_data.nombre) : 'Curso'"
                item-value="cruso_data.IDF"
                placeholder="Selecciona el curso a pagar..."
                outlined
                rounded
                dense
                color="primary"
                prepend-inner-icon="mdi-book-education-outline"
                return-object
                @change="loadData"
                :loading="loadingCursos"
                hide-details
                class="mt-2"
              ></v-select>
            </v-card>

            <!-- Payments List Section -->
            <v-expand-transition>
              <div v-if="curso && pagos.items && pagos.items.length > 0" class="mt-4">
                
                <div class="d-flex align-center justify-space-between mb-3 px-1">
                  <div class="text-subtitle-1 font-weight-bold primary-text d-flex align-center">
                    <v-icon color="primary" small class="mr-2">mdi-format-list-checks</v-icon>
                    Selecciona lo que deseas pagar
                  </div>
                  <v-chip small color="primary" outlined class="font-weight-bold">
                    {{ pagos.items.length }} {{ pagos.items.length === 1 ? 'ítem' : 'ítems' }}
                  </v-chip>
                </div>

                <v-card class="rounded-xl border-light overflow-hidden bg-white mb-6" elevation="0">
                  <v-list class="pa-0 bg-transparent">
                    <template v-for="(pagoData, pagoKey) in pagos.items">
                      <v-list-item
                        :key="pagoKey"
                        class="py-3 px-4 border-bottom transition-all item-row"
                        :class="{'bg-selected': pagoData.check, 'item-disabled': pagoData.block && !pagoData.pagado}"
                      >
                        <!-- Checkbox / Icon Status -->
                        <v-list-item-action class="mr-3 mr-sm-4 my-0">
                          <v-checkbox
                            v-if="canPay(pagoData)"
                            v-model="pagoData.check"
                            color="primary"
                            @change="changeTotal(pagoData, pagoKey)"
                            hide-details
                            class="ma-0 pa-0"
                          ></v-checkbox>
                          <v-avatar v-else-if="pagoData.pagado" size="32" color="success lighten-5">
                            <v-icon color="success" small>mdi-check-circle</v-icon>
                          </v-avatar>
                          <v-avatar v-else size="32" color="grey lighten-4">
                            <v-icon color="grey" small>mdi-lock</v-icon>
                          </v-avatar>
                        </v-list-item-action>

                        <!-- Concept Details -->
                        <v-list-item-content class="py-1">
                          <div class="d-flex align-center flex-wrap gap-2">
                            <span class="font-weight-bold text-body-1 mr-2" :class="getTextColorClass(pagoData)">
                              {{ pagoData.Materia }}
                            </span>
                            <v-chip
                              v-if="pagoData.beca != 0"
                              x-small
                              color="success lighten-5"
                              text-color="success darken-2"
                              class="font-weight-bold mr-1"
                            >
                              <v-icon left x-small>mdi-tag</v-icon>
                              Beca {{ pagoData.beca }}%
                            </v-chip>
                            <v-chip
                              x-small
                              :color="pagoData.pagado ? 'success lighten-5' : (pagoData.block ? 'grey lighten-4' : 'warning lighten-5')"
                              :text-color="pagoData.pagado ? 'success darken-2' : (pagoData.block ? 'grey darken-2' : 'warning darken-3')"
                              class="font-weight-bold"
                            >
                              {{ pagoData.pagado ? 'Pagado' : (pagoData.block ? 'Bloqueado' : 'Pendiente') }}
                            </v-chip>
                          </div>
                          <div v-if="pagoData.block && !pagoData.pagado" class="text-caption grey--text mt-1 d-flex align-center">
                            <v-icon x-small color="grey" class="mr-1">mdi-lock-outline</v-icon>
                            Requiere tener al día las cuotas anteriores
                          </div>
                        </v-list-item-content>

                        <!-- Price -->
                        <v-list-item-action class="my-0">
                          <div class="text-right">
                            <div class="text-h6 font-weight-bold" :class="getTextColorClass(pagoData)">
                              <span v-if="pagoData.pagado">${{ pagoData.precioPagado }}</span>
                              <span v-else>${{ pagoData.precioAPagar }}</span>
                            </div>
                          </div>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider :key="'div-'+pagoKey" v-if="pagoKey < pagos.items.length - 1"></v-divider>
                    </template>

                    <!-- Commission -->
                    <v-list-item class="py-3 px-4 bg-orange-soft border-bottom">
                      <v-list-item-content class="py-0">
                        <div class="d-flex align-center">
                          <v-icon small color="orange darken-3" class="mr-2">mdi-shield-check-outline</v-icon>
                          <span class="font-weight-bold text-subtitle-2 orange--text text--darken-4">COMISIÓN DE PLATAFORMA</span>
                        </div>
                      </v-list-item-content>
                      <v-list-item-action class="my-0">
                        <v-chip x-small color="orange darken-3" dark class="font-weight-bold">0% BONIFICADO</v-chip>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Total -->
                    <v-list-item class="py-4 px-4 bg-green-soft">
                      <v-list-item-content class="py-0">
                        <div class="text-h6 font-weight-bold success--text text--darken-3">
                          TOTAL A PAGAR
                        </div>
                      </v-list-item-content>
                      <v-list-item-action class="my-0">
                        <div class="text-h5 font-weight-black success--text text--darken-3">
                          ${{ total }}
                        </div>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card>

                <!-- Action Button -->
                <div class="d-flex justify-end mt-4">
                  <v-btn
                    color="primary"
                    rounded
                    depressed
                    x-large
                    class="px-8 shadow-premium text-none font-weight-bold"
                    @click="pay()"
                    :disabled="total == 0 || paying"
                    :loading="paying"
                  >
                    <v-icon left>mdi-credit-card-check-outline</v-icon>
                    Efectuar Pago (${{ total }})
                  </v-btn>
                </div>
              </div>
            </v-expand-transition>

            <!-- Loading Payments Skeleton -->
            <div v-if="loadingPagos" class="pa-8 text-center bg-white rounded-xl mt-4 border-light">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
              <div class="text-caption grey--text mt-3 font-weight-medium">Cargando aranceles...</div>
            </div>

            <!-- Empty State: Course selected but no payments pending -->
            <div v-if="curso && !loadingPagos && (!pagos.items || pagos.items.length === 0)" class="text-center pa-10 bg-white rounded-xl mt-4 border-light">
              <v-avatar color="success lighten-5" size="80" class="mb-4">
                <v-icon size="48" color="success">mdi-check-circle-outline</v-icon>
              </v-avatar>
              <h3 class="text-h6 font-weight-bold primary-text">¡Estás al día!</h3>
              <p class="text-body-2 grey--text text--darken-1 mb-0">No posees cuotas ni aranceles pendientes de pago para este curso.</p>
            </div>

            <!-- Empty State: No course selected -->
            <div v-if="!curso && !loadingCursos" class="text-center pa-10 bg-white rounded-xl mt-4 border-light">
              <v-avatar color="primary lighten-5" size="80" class="mb-4">
                <v-icon size="40" color="primary">mdi-credit-card-search-outline</v-icon>
              </v-avatar>
              <h3 class="text-subtitle-1 font-weight-bold primary-text">Selecciona un curso</h3>
              <p class="text-caption grey--text text--darken-1 mb-0">Elige un programa en el menú superior para ver tus aranceles y cuotas.</p>
            </div>

          </div>
        </v-card>
      </v-col>
    </CardIeaAppMain>

    <!-- Maintenance View -->
    <CardIeaAppMain colorCard="transparent" title="" class="premium-wrapper" v-else>
      <v-col cols="12" class="pa-0">
        <div class="hero-header glass-panel mb-4 py-3 px-6 mx-2 mx-sm-4 mt-4">
          <div class="d-flex align-center justify-space-between w-100">
            <div class="d-flex align-center">
              <v-btn icon dark color="primary" class="mr-4 glass-btn" @click="$router.go(-1)" v-if="user && user.IDStB">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <div>
                <h1 class="text-h5 font-weight-bold primary-text mb-0">Servicio en Mantenimiento</h1>
                <p class="text-caption grey--text text--darken-1 mb-0">Estamos actualizando la pasarela de pagos</p>
              </div>
            </div>
            <v-icon size="48" color="primary" class="opacity-30">mdi-tools</v-icon>
          </div>
        </div>
        <v-card class="glass-card rounded-xl overflow-hidden mx-2 mx-sm-4 mb-4 pa-6" elevation="10">
          <v-alert prominent type="error" class="rounded-xl mb-6">
            Servicio en mantenimiento, pronto estaremos en línea. Gracias por tu paciencia.
          </v-alert>
          <v-text-field
            v-model="isadmin"
            label="Contraseña de administración"
            outlined
            rounded
            dense
            type="password"
            @input="up"
            style="max-width: 280px;"
          ></v-text-field>
        </v-card>
      </v-col>
    </CardIeaAppMain>
  </div>
</template>

<script>
import CardIeaAppMain from "../../components/CardIeaAppMain";

export default {
  name: "PaymentCurso",
  components: {
    CardIeaAppMain,
  },

  data: function() {
    return {
      title: "Efectuar pago",
      curso: null,
      cursos: [],
      pagos: [],
      total: 0,
      items: [],
      urlPay: '',
      mantenimiento: false,
      isadmin: '',
      isSuscribedDA: false,
      isSuscribedMB: false,
      loadingCursos: false,
      loadingPagos: false,
      paying: false
    };
  },

  computed: {
    user() {
      return this.$store.getters.user;
    }
  },

  mounted() {
    this.getIsSuscribedToDA();

    this.loadingCursos = true;
    this.$API
      .get({
        url: "alumno/cursos",
        data: {},
      })
      .then((res) => {
        this.cursos = Array.isArray(res) ? res : [];
      })
      .catch((err) => {
        console.error("Error al cargar cursos:", err);
      })
      .finally(() => {
        this.loadingCursos = false;
      });
  },

  methods: {
    up() {
      if (this.isadmin === 'adminadmin') {
        this.mantenimiento = false;
      }
    },

    getIsSuscribedToDA() {
      if (!this.user || !this.user.IDStB) return;
      this.$API
        .get({
          url: "mp-getsuscription-status/" + this.user.IDStB,
          data: {},
        })
        .then((res) => {
          this.isSuscribedDA = !!res.isSuscribedMP;
          this.isSuscribedMB = !!res.isSuscribedMB;
        })
        .catch((err) => {
          console.error("Error suscripcion:", err);
        });
    },

    async loadData() {
      if (!this.curso || !this.curso.Curso) return;
      this.loadingPagos = true;
      this.pagos = [];
      this.total = 0;
      this.items = [];

      this.$API
        .get({
          url: "listado-pagos/" + this.curso.Curso,
          data: {},
        })
        .then((res) => {
          this.pagos = res || { items: [] };
          this.total = 0;
        })
        .catch((err) => {
          console.error("Error al cargar cuotas:", err);
        })
        .finally(() => {
          this.loadingPagos = false;
        });
    },

    getTextColorClass(pagoData) {
      if (pagoData.block && !pagoData.pagado) return 'grey--text';
      if (pagoData.pagado) return 'success--text text--darken-2';
      return 'primary-text';
    },

    canPay(pagoData) {
      return !pagoData.block && !pagoData.pagado;
    },

    changeTotal(pagoData, index) {
      pagoData.index = index;
      if (pagoData.check) {
        this.total += parseFloat(pagoData.precioAPagar) || 0;
      } else {
        this.total -= parseFloat(pagoData.precioAPagar) || 0;
      }
      this.total = Math.max(0, Math.round(this.total * 100) / 100);

      const items = (this.pagos.items || []).filter(d => d.check);
      this.items = items.map(value => ({
        id: value.IDStB,
        monto: value.precioAPagarDesc || value.precioAPagar,
        index: value.index
      }));
    },

    async pay() {
      if (this.total === 0 || this.paying) return;

      const sedeNombre = (this.curso && this.curso.cruso_data && this.curso.cruso_data.sede)
        ? (this.curso.cruso_data.sede.razonSocial || this.curso.cruso_data.sede.Feriado || '')
        : '';

      const dataSend = {
        curso: this.curso.Curso,
        alumno: this.user.IDStB,
        items: this.items,
        sede: sedeNombre,
      };

      this.paying = true;
      const url = "mobbex/generar-link-pago-materias";

      try {
        const res = await this.$API.post({
          url: url,
          data: dataSend
        });

        if (res.error) {
          alert(res.error);
          return;
        }

        if (res.data && res.data.url) {
          location.href = res.data.url;
        } else if (typeof res === 'string' && res.startsWith('http')) {
          location.href = res;
        } else {
          alert("No se pudo obtener el enlace de pago. Por favor intente nuevamente.");
        }
      } catch (err) {
        console.error("Error al procesar pago:", err);
        alert("Ocurrió un error al procesar el pago.");
      } finally {
        this.paying = false;
      }
    }
  }
};
</script>

<style scoped>
.payment-container {
  background: transparent !important;
  min-height: calc(100vh - 120px);
}

.glass-panel {
  background: rgba(255, 255, 255, 0.75) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.05) !important;
  border-radius: 20px;
}

.glass-card {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
}

.glass-btn {
  background: rgba(25, 118, 210, 0.1) !important;
  backdrop-filter: blur(4px) !important;
  box-shadow: none !important;
  transition: all 0.3s ease;
}
.glass-btn:hover {
  background: rgba(25, 118, 210, 0.2) !important;
  transform: translateY(-2px);
}

.content-area {
  border-radius: inherit;
}

.bg-light { background-color: #f8fafc !important; }
.bg-selected { background-color: #eff6ff !important; }
.bg-orange-soft { background-color: #fff7ed !important; }
.bg-green-soft { background-color: #f0fdf4 !important; }

.item-row {
  transition: background-color 0.2s ease;
}
.item-row:hover:not(.item-disabled) {
  background-color: #f1f5f9;
}
.item-disabled {
  opacity: 0.7;
}

.border-light { border: 1px solid rgba(0, 0, 0, 0.06) !important; }
.border-bottom { border-bottom: 1px solid rgba(0, 0, 0, 0.06); }

.shadow-premium {
  box-shadow: 0 8px 24px -6px rgba(25, 118, 210, 0.45) !important;
}

.primary-text { color: #1e293b; }
.opacity-30 { opacity: 0.3; }
.gap-2 { gap: 8px; }
</style>
